import { DOCUMENT_REQUEST_CATEGORY } from '../constants'
import {
  getDocumentRequestCategoryKey,
  isDocumentRequestCompleted,
} from './DocumentCenterPageUtils'

/** Group uploads by document request id (one section per request, even within same category). */
export const buildUploadsByRequestId = (uploadHistoryByCategory) => {
  const map = new Map()
  ;(uploadHistoryByCategory || []).forEach((group) => {
    const items = group.uploads || group.items || []
    const groupRequestId = group.documentRequestId ?? group.id
    items.forEach((item) => {
      const requestId = item.documentRequestId ?? groupRequestId ?? null
      const key = requestId != null ? String(requestId) : '__no_request__'
      if (!map.has(key)) map.set(key, [])
      map.get(key).push({
        ...item,
        _categoryKey: group.category ?? group.categoryKey ?? group.documentType ?? group.name,
      })
    })
  })
  return map
}

export const buildUploadHistorySections = ({ allDocumentRequestsSorted, uploadsByRequestId }) => {
  const sections = []
  const coveredRequestIds = new Set()

  ;(allDocumentRequestsSorted || []).forEach((request) => {
    const requestId = request.id ?? request.documentRequestId
    const key = requestId != null ? String(requestId) : null
    if (key) coveredRequestIds.add(key)
    const uploads = key ? uploadsByRequestId.get(key) || [] : []
    if (uploads.length > 0) {
      sections.push({ request, uploads })
    }
  })

  uploadsByRequestId.forEach((uploads, key) => {
    if (key === '__no_request__' || coveredRequestIds.has(key)) return
    if (uploads.length === 0) return
    const categoryKey = uploads[0]?._categoryKey ?? '__other__'
    sections.push({
      request: null,
      uploads,
      isOther: true,
      categoryKey,
    })
  })

  const otherUploads = uploadsByRequestId.get('__no_request__') || []
  if (otherUploads.length > 0) {
    const byCategory = new Map()
    otherUploads.forEach((item) => {
      const cat = item._categoryKey ?? 'Other uploads'
      if (!byCategory.has(cat)) byCategory.set(cat, [])
      byCategory.get(cat).push(item)
    })
    byCategory.forEach((uploads, categoryKey) => {
      sections.push({
        request: null,
        uploads,
        isOther: true,
        categoryKey,
      })
    })
  }
  return sections
}

export const buildUploadHistoryByCategoryGrouped = (uploadHistorySections) => {
  const byCategory = new Map()
  ;(uploadHistorySections || []).forEach((section) => {
    const { request, isOther, categoryKey: sectionCategoryKey } = section
    const categoryKey = isOther
      ? sectionCategoryKey ?? '__other__'
      : getDocumentRequestCategoryKey(request) ?? '__other__'
    if (!byCategory.has(categoryKey)) byCategory.set(categoryKey, [])
    byCategory.get(categoryKey).push(section)
  })
  byCategory.forEach((sectionList) => {
    sectionList.sort((a, b) => {
      const aDone = isDocumentRequestCompleted(a.request)
      const bDone = isDocumentRequestCompleted(b.request)
      if (aDone === bDone) return 0
      return aDone ? 1 : -1
    })
  })
  return byCategory
}

/**
 * Category order: (0) no completed requests in group → (1) mixed → (2) all completed.
 */
export const buildUploadHistoryCategoryEntries = (uploadHistoryByCategoryGrouped) => {
  const entries = Array.from(uploadHistoryByCategoryGrouped.entries())
  const categorySortKey = ([, sectionList]) => {
    if (sectionList.length === 0) return 0
    const allCompleted = sectionList.every((s) => isDocumentRequestCompleted(s.request))
    if (allCompleted) return 2
    const anyCompleted = sectionList.some((s) => isDocumentRequestCompleted(s.request))
    if (anyCompleted) return 1
    return 0
  }
  return [...entries].sort((a, b) => {
    const ka = categorySortKey(a)
    const kb = categorySortKey(b)
    if (ka !== kb) return ka - kb
    return String(a[0]).localeCompare(String(b[0]))
  })
}

export const splitEmployeeAndSystemGeneratedCategoryEntries = (
  uploadHistoryCategoryEntries,
  systemGeneratedCategory = DOCUMENT_REQUEST_CATEGORY.SYSTEM_GENERATED
) => {
  const employeeEntries = []
  let systemGeneratedSections = []

  ;(uploadHistoryCategoryEntries || []).forEach(([categoryKey, sections]) => {
    if (String(categoryKey) === systemGeneratedCategory) {
      systemGeneratedSections = sections
    } else {
      employeeEntries.push([categoryKey, sections])
    }
  })

  return { employeeEntries, systemGeneratedSections }
}

export const countUploadFilesInSections = (sections) =>
  (sections || []).reduce((sum, section) => sum + (section.uploads?.length ?? 0), 0)

export const getDefaultSelectedCategoryKey = (employeeCategoryEntries) =>
  employeeCategoryEntries?.[0]?.[0] ?? null

export const documentRequestHasUploads = (action, uploadsByRequestId) => {
  if (!action) return false
  const uploads = action.documentUploads ?? action.uploads ?? []
  if (Array.isArray(uploads) && uploads.length > 0) return true
  const requestId = action.id ?? action.documentRequestId
  if (requestId != null) {
    const historyForRequest = uploadsByRequestId.get(String(requestId))
    if (Array.isArray(historyForRequest) && historyForRequest.length > 0) return true
  }
  return false
}

export const flattenSystemGeneratedDocuments = (
  uploadHistoryByCategory,
  systemGeneratedCategory = DOCUMENT_REQUEST_CATEGORY.SYSTEM_GENERATED
) => {
  const documents = []
  ;(uploadHistoryByCategory || []).forEach((group) => {
    const categoryKey =
      group.category ?? group.categoryKey ?? group.documentType ?? group.name ?? null
    if (String(categoryKey) !== systemGeneratedCategory) return
    const items = group.uploads || group.items || []
    items.forEach((item) => documents.push(item))
  })
  return documents
}

export const getUploadDisplayFileName = (item) => item?.originalFileName ?? 'Document'

export const getUploadDisplayDate = (item) => {
  const raw = item?.uploadedAt || item?.createdAt
  return raw ? new Date(raw).toLocaleDateString() : '—'
}

export const getUploadDocumentId = (item) => item?.documentUploadId ?? item?.id ?? null
